import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';
import React, { useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';
import IconButton from '@components/IconButton';

import { useFeatureFlags } from '@hooks/useFeatureFlags';

import { ShareType } from 'generated/types';

import CreateShareLinkDialogContent from './CreateShareLinkDialogContent';
import { ShareLinkDialogContent } from './ShareLinkDialogContent';

const messages = defineMessages({
  share: {
    id: 'shareButton.share',
    defaultMessage: 'Share',
  },
});

interface ShareButtonProps {
  /**
   * The ID of the patient associated with the share link.
   */
  vitall_patient_id: string;
  /**
   * The ID of the resource model associated with the share link.
   */
  resource_model_id?: string;
  /**
   * The type of share link to create. Defaults to `SingleResource`.
   */
  share_type?: ShareType;
  /**
   * The DHA being shared. Required for `ShareType.DigitalHealthAssessment`.
   */
  assessment_id?: string;
  /**
   * The color of the share button.
   */
  color?: string;
}

/**
 * ShareButton component is responsible for rendering a button that, when clicked,
 * opens a dialog for creating a share link.
 *
 * @param {Object} props - Component props.
 * @returns {JSX.Element} The rendered ShareButton component.
 */
function ShareButton({
  resource_model_id,
  vitall_patient_id,
  share_type = ShareType.SingleResource,
  color = 'primary.contrastText',
  assessment_id,
}: ShareButtonProps) {
  const { formatMessage } = useIntl();
  const { flags } = useFeatureFlags();
  // State to track the dialog open/close status
  const [open, setOpen] = useState(false);
  // State to track the expiration time of the share link
  const [expires, setExpires] = useState<number>();
  // State to track the list of email addresses for the share link
  const [recipients, setRecipients] = useState<string[]>();

  /**
   * Handler for opening the dialog.
   */
  const handleOpen = () => {
    setOpen(true);
    setExpires(undefined);
    setRecipients(undefined);
  };

  /**
   * Handler for closing the dialog.
   */
  const handleClose = (reason?: string) => {
    // Do not close the dialog if the backdrop is clicked
    if (reason === 'backdropClick') {
      return;
    }
    setOpen(false);
  };

  /**
   * Handler for creating a share link.
   *
   * @param {Object} params - Parameters for creating a share link.
   * @param {number} params.expires - The expiration time of the share link.
   * @param {string[]} params.emails - The list of email addresses for the share link.
   */
  const handleCreateShareLink = async ({
    expires,
    emails,
  }: {
    emails: string[];
    expires: number;
  }) => {
    setExpires(expires);
    setRecipients(emails);
  };

  // Check if the component is in the creating state
  const isCreating = !recipients?.length || !expires;

  // DHA sharing is a separate backend permission (so practitioners and org
  // admins can share a DHA without gaining every other share type), and so it
  // surfaces as its own feature flag.
  const isEnabled =
    share_type === ShareType.DigitalHealthAssessment
      ? flags.isSharingCreateDhaShareLinkEnabled
      : flags.isSharingCreateShareLinkEnabled;

  if (!isEnabled) {
    return null;
  }

  return (
    <>
      {/* Render the share button */}
      <Button
        variant="text"
        color="primary"
        onClick={handleOpen}
        startIcon={<Icon color={color} name="share-nodes" type="fas" />}
        sx={{ color: color }}
      >
        {formatMessage(messages.share)}
      </Button>
      {/* Render the dialog */}
      <Dialog
        open={open}
        onClose={(_e, reason) => handleClose(reason)}
        maxWidth={false}
        PaperProps={{ sx: { maxWidth: 500 } }}
      >
        {/* Render the dialog title */}
        <DialogTitle>{formatMessage(messages.share)}</DialogTitle>
        <IconButton
          onClick={() => handleClose()}
          name="xmark"
          iconType="fas"
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        />
        <Divider />
        <DialogContent>
          {/* Render the create share link dialog content if in the creating state */}
          {isCreating && (
            <CreateShareLinkDialogContent onSubmit={handleCreateShareLink} />
          )}
          {/* Render the share link dialog content if not in the creating state */}
          {!isCreating && (
            <ShareLinkDialogContent
              assessment_id={assessment_id}
              expires={expires}
              recipients={recipients}
              resource_model_id={resource_model_id}
              vitall_patient_id={vitall_patient_id}
              onClose={handleClose}
              share_type={share_type}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default withErrorBoundary(ShareButton, {
  fallback: null,
});
